/**
 * Sugestão de correção para erro de digitação no domínio do e-mail (P-23,
 * ADR-0014).
 *
 * Por que isto existe, e não só a confirmação por e-mail: **confirmar prova
 * posse, não previne typo.** Quem digita `gmial.com` só descobre o erro quando
 * o e-mail não chega — e, no fluxo novo, já pagou. A sugestão age no único
 * momento em que a pessoa ainda pode corrigir: com o campo na frente dela.
 *
 * Sugere, nunca corrige sozinho. `joao@gmai.com.br` pode ser um domínio real, e
 * trocar o que a pessoa digitou por trás dela é pior do que o erro.
 */

/**
 * Domínios de e-mail pessoais mais comuns no Brasil. A lista é curta de
 * propósito: ela existe para pegar o dedo escorregando numa marca conhecida,
 * não para catalogar provedores. Domínio corporativo nunca vai estar aqui, e
 * por isso a distância mínima evita palpite em cima do que não conhecemos.
 */
const DOMINIOS_COMUNS = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'outlook.com.br',
  'yahoo.com',
  'yahoo.com.br',
  'icloud.com',
  'live.com',
  'bol.com.br',
  'uol.com.br',
  'terra.com.br',
  'globo.com',
  'me.com',
  'protonmail.com',
];

/** Distância de Levenshtein, iterativa e com duas linhas — basta para domínios. */
function distancia(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let anterior = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i += 1) {
    const atual = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const custo = a[i - 1] === b[j - 1] ? 0 : 1;
      atual[j] = Math.min(atual[j - 1] + 1, anterior[j] + 1, anterior[j - 1] + custo);
    }
    anterior = atual;
  }

  return anterior[b.length];
}

/**
 * Devolve o e-mail corrigido quando o domínio parece um erro de digitação de um
 * domínio conhecido, ou `null` quando não há palpite seguro.
 *
 * A distância máxima é 2, e só para domínios de tamanho parecido: sem isso
 * `bol.com.br` viraria sugestão para qualquer domínio curto, e a sugestão
 * errada custa mais que a ausência dela.
 */
export function sugerirCorrecaoDeEmail(valor: string): string | null {
  const limpo = valor.trim().toLowerCase();
  const arroba = limpo.lastIndexOf('@');
  if (arroba < 1 || arroba === limpo.length - 1) return null;

  const usuario = limpo.slice(0, arroba);
  const dominio = limpo.slice(arroba + 1);

  // Já é um domínio conhecido: nada a sugerir.
  if (DOMINIOS_COMUNS.includes(dominio)) return null;

  let melhor: { dominio: string; distancia: number } | null = null;

  for (const candidato of DOMINIOS_COMUNS) {
    // Diferença grande de tamanho quase nunca é typo, e é onde moram os falsos
    // positivos em cima de domínio corporativo.
    if (Math.abs(candidato.length - dominio.length) > 2) continue;

    const d = distancia(dominio, candidato);
    if (d > 0 && d <= 2 && (!melhor || d < melhor.distancia)) {
      melhor = { dominio: candidato, distancia: d };
    }
  }

  return melhor ? `${usuario}@${melhor.dominio}` : null;
}
